<template>
  <div :class="`date-interval-picker date-interval-picker_${layout}`">
    <Form
      ref="formRef"
      :rules="formRules"
      :model="state"
      :layout="ELayout.vertical"
    >
      <div
        v-if="state.isDateInterval"
        class="date-interval-picker__wrapper"
      >
        <FormItem
          name="intervalValue"
          class="date-interval-picker__item"
          :class="{
            'date-interval-picker__item_without-calendar': !!state.dateStart || isEditableField,
            'date-interval-picker__item_inline': isEditableField
          }"
        >
          <RangePicker
            dropdownClassName="time-range-picker"
            :placeholder="[capitalize(tt('shared.since')), capitalize(tt('shared.by'))]"
            :open="state.isPickerOpened"
            :value="rangePickerValue"
            :disabledDate="getDisabledDate"
            :format="displayFormat"
            :disabled="[disabledStart, disabledEnd]"
            :allowClear="!isEditableField && !!state.dateStart && allowClear"
            :getPopupContainer="customPopupContainer ? getPopupContainer : undefined"
            :showTime="rangePickerWithTime"
            @change="handleRangeDatesChange"
            @openChange="handlePickerOpenChange"
          />
        </FormItem>
      </div>
      <div
        v-else
        class="date-interval-picker__wrapper"
      >
        <FormItem
          name="exactValue"
          :class="{
            'date-interval-picker__item date-interval-picker__item_without-calendar': isEditableField,
            'date-interval-picker__item_inline': isEditableField
          }"
        >
          <DatePicker
            dropdownClassName="time-range-picker"
            :value="state.dateStart || undefined"
            :format="displayFormat"
            :disabled="disabledStart"
            :open="state.isPickerOpened"
            :allowClear="!isEditableField && !!state.dateStart && allowClear"
            :getPopupContainer="getPopupContainer"
            :showTime="rangePickerWithTime"
            :disabledDate="getDisabledDate"
            @change="handleExactDateChange"
            @openChange="handlePickerOpenChange"
          />
        </FormItem>
      </div>
    </Form>
    <Checkbox
      v-if="showCheckbox"
      :class="`date-interval-picker__checkbox date-interval-picker__checkbox_${layout}`"
      :checked="state.isDateInterval"
      @change="handleCheckboxChange($event.target.checked)"
    >
      {{ tt('shared.dateInterval') }}
    </Checkbox>
  </div>
</template>

<script lang="ts">
import {
  DatePicker,
  RangePicker,
  Form,
  FormItem,
} from 'ant-design-vue';
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  reactive,
  ref,
  toRaw,
  watch,
} from 'vue';

import { Checkbox } from '@/ui';
import { DATE_FORMAT, ELayout } from '@/constants';
import { EDateTimeType } from '@/domains/constants';
import { TPickerOptions, TDateAppFormat } from '@/domains/types';
import { useFormHandlers } from '@/composables/useFormHandlers';
import tt from '@/i18n/utils/translateText';
import { capitalize } from '@/utils';

import { EIntervalMode } from '../IntervalPicker/domain/constants';

export default defineComponent({
  name: 'DateIntervalPicker',
  components: {
    Checkbox,
    DatePicker,
    RangePicker,
    Form,
    FormItem,
  },
  props: {
    date: {
      type: Object as PropType<TPickerOptions>,
      default: null,
    },

    displayFormat: {
      type: String,
      default: DATE_FORMAT.fullDate,
    },

    outputFormat: {
      type: String,
      default: DATE_FORMAT.UTCformat,
    },

    isEditableField: {
      type: Boolean,
      default: false,
    },

    disabledStart: {
      type: Boolean,
      default: false,
    },

    disabledEnd: {
      type: Boolean,
      default: false,
    },

    disabledDateRuleFn: {
      type: Function as PropType<(_date: TDateAppFormat) => boolean>,
      default: () => false,
    },

    allowEqualStartAndEndDays: {
      type: Boolean,
      default: true,
    },

    rangePickerWithTime: {
      type: Boolean,
      default: false,
    },

    errors: {
      type: Object,
      default: () => ({}),
    },

    mode: {
      type: String as PropType<EIntervalMode>,
      default: EIntervalMode.singleAndInterval,
    },

    layout: {
      type: String as PropType<ELayout>,
      default: ELayout.vertical,
    },

    customPopupContainer: {
      type: Boolean,
      default: true,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const formRef = ref<typeof Form | null>(null);

    const rules = {
      exactValue: [],
      intervalValue: [],
    };

    const { formRules, onControlChange, onError } = useFormHandlers(formRef, { rules });

    const setError = () => {
      onError({ errors: props.errors });
    };

    watch(() => props.errors, setError);

    const singleAndIntervalMode = computed(() => props.mode === EIntervalMode.singleAndInterval);

    /**
     * Ant RangePicker в качестве value принимает только значения таких типов, как - [string, string], [dayjs, dayjs] и undefined.
     * Те или выбраны обе даты или ни одной (undefined). Но в то же время, корректно работает, если передать только одну дату [dayjs, undefined].
     * Можно было бы сделать таким образом: state.dateStart && state.dateEnd ? [state.dateStart, state.dateEnd] : undefined , но нет уверенности в том,
     * что в пропсах всегда прилетают обе даты и не заблочить такой вариант [dayjs, undefined].
     * */
    const rangePickerValue = computed(() => [state.dateStart, state.dateEnd] as [TDateAppFormat, TDateAppFormat]);

    const getInitialIsInterval = (mode: EIntervalMode, isInterval: boolean) => {
      if (mode === EIntervalMode.single) return false;
      if (mode === EIntervalMode.interval) return true;
      return isInterval;
    };

    const state = reactive({
      isDateInterval: getInitialIsInterval(props.mode, props.date?.interval),
      dateStart: props.date?.start as TDateAppFormat | null,
      dateEnd: props.date?.end as TDateAppFormat | null,
      isPickerOpened: false,
    });

    const setDateFromProps = () => {
      state.isDateInterval = getInitialIsInterval(props.mode, props.date?.interval);
      state.dateStart = props.date?.start;
      state.dateEnd = props.date?.end;
    };

    const changeIsDateStartOpenedFromIsEditModeProp = () => {
      // Если isEditableField: true => datePicker всегда находится в edit mode (в режиме просмотра это просто текст)
      if (props.isEditableField) {
        state.isPickerOpened = true;
      }
    };

    watch(() => [props.date?.start, props.date?.end, props.date?.interval], setDateFromProps);

    const handleChange = () => {
      const format = props.rangePickerWithTime ? DATE_FORMAT.UTCWithTimeFormat : props.outputFormat;
      const { dateStart, dateEnd } = toRaw(state);

      emit('change', {
        start: dateStart?.format(format) || null,
        end: dateEnd?.format(format) || null,
        type: state.isDateInterval ? EDateTimeType.interval : EDateTimeType.determined,
      });
    };

    const handleCheckboxChange = (checked: boolean) => {
      state.isDateInterval = checked;
      handleChange();
    };

    // TODO При замене Antd компонентов на компоненты из UI изменить тип `string | TDateAppFormat` -> `TDateAppFormat` (если будет актуально)
    const handleExactDateChange = (date: string | TDateAppFormat) => {
      state.dateStart = date as TDateAppFormat;
      onControlChange({ name: 'exactValue' });
      handleChange();
    };

    // TODO При замене Antd компонентов на компоненты из UI изменить тип `[TDateAppFormat, TDateAppFormat] | [string, string]` -> `TDateAppFormat[]` (если будет актуально)
    const handleRangeDatesChange = (value: [TDateAppFormat, TDateAppFormat] | [string, string]) => {
      const dates = value as TDateAppFormat[];

      if (!dates) {
        state.dateStart = null;
        state.dateEnd = null;
      } else {
        const [startDate, endDate] = dates;
        // проверяем на ограничение выбора одной и той же даты начала и окончания и зануляем при их равенстве.
        // это не получается вынести в getDisabledDate, так как @change срабатывает только
        // при изменении интервала дат (обе даты должны быть выбраны)
        if (!props.allowEqualStartAndEndDays && startDate.isSame(endDate, 'day')) {
          state.dateStart = null;
          state.dateEnd = null;
        } else {
          state.dateStart = startDate;
          state.dateEnd = endDate;
        }
        // событие @change срабатывает при изменении интервала дат, при этом,
        // если было изменено только одно значение, событие @openChange не отработает,
        // в этом случае нужно принудительно закрывать календарь после того, как был изменен интервал
        // важно в кейсе, когда предустановлено начало или конец интервала и пользователь задает только недостающую дату
        state.isPickerOpened = false;
      }
      onControlChange({ name: 'intervalValue' });
      handleChange();
    };

    const handlePickerOpenChange = (openState: boolean) => {
      state.isPickerOpened = openState;
    };

    // у ant rangePicker есть валидация на то,
    // чтобы дата начала не была позже даты окончания и наоборот.
    // поэтому дизейблить даты в календаре будем только на основании кастомной функции-валидатора
    const getDisabledDate = (date: TDateAppFormat) => props.disabledDateRuleFn(date);

    const getPopupContainer = (triggerNode: any) => triggerNode.parentNode;

    const showCheckbox = computed(() => !props.isEditableField && singleAndIntervalMode.value);

    onMounted(changeIsDateStartOpenedFromIsEditModeProp);

    return {
      formRules,
      formRef,
      state,
      rangePickerValue,
      showCheckbox,
      ELayout,

      handleCheckboxChange,
      handleExactDateChange,
      setDateFromProps,
      changeIsDateStartOpenedFromIsEditModeProp,
      getPopupContainer,
      getDisabledDate,
      handleRangeDatesChange,
      handlePickerOpenChange,
      tt,
      capitalize,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
